import React, { useState, useContext } from 'react';
import {
  TableCell,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { SchoolAdmin } from '@klab-berlin/api-sdk/lib/types/responses/Teacher';
import { selectSelectedSchoolId, selectUser } from '../../../../reducers/user.selectors';
import { useSelector } from 'react-redux';
import ConfirmDialog from '../../../Dialog/ConfirmationDialog';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import schoolService from '../../../../services/school.service';

const SchoolAdminItem: React.FC<{
  admin: SchoolAdmin;
  onDelete: (adminId: string) => void;
}> = ({ admin, onDelete }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const { openSnackbar } = useContext(SnackbarContext);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isResending, setIsResending] = useState(false);

  const showDeleteButton: boolean = user?.id !== admin._id;
  const isActive: boolean = admin.active;
  const statusLabel = isActive ? t('Active') : t('Not active');
  const statusColor = isActive ? 'success.main' : 'error.main';
  const menuOpen = Boolean(menuAnchorEl);

  const handleDeleteClick = () => {
    setConfirmDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    onDelete(admin._id);
    setConfirmDialogOpen(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleResendInvitation = async () => {
    if (isResending) return;

    setIsResending(true);
    try {
      if (!selectedSchoolId) return;
      // Use the existing resendInvitation method with a default license model ID
      // You may need to adjust this based on your school admin license model
      const result = await schoolService.resendInvitation(selectedSchoolId, admin._id); // Default to mU Pro license

      if (result?.success === true) {
        openSnackbar({
          type: 'success',
          message: t('Invitation email sent successfully')
        });
      } else {
        openSnackbar({
          type: 'error',
          message: t('Error while sending an email')
        });
      }
    } catch (error) {
      openSnackbar({
        type: 'error',
        message: t('Error while sending an email')
      });
    } finally {
      setIsResending(false);
      handleMenuClose();
    }
  };

  return (
    <>
      <TableRow sx={{ height: '73px' }}>
        <TableCell
          sx={{ maxWidth: 260, overflowX: 'auto', whiteSpace: 'nowrap', p: 0 }}
        >
          <Box sx={{ display: 'block', minWidth: '100%', px: 2 }}>
            {admin.firstName} {admin.lastName}
          </Box>
        </TableCell>
        <TableCell
          sx={{ maxWidth: 430, overflowX: 'auto', whiteSpace: 'nowrap', p: 0 }}
        >
          <Box sx={{ display: 'block', minWidth: '100%', px: 2 }}>
            {admin.email}
          </Box>
        </TableCell>
        <TableCell>
          <Typography color={statusColor} component='span'>
            {statusLabel}
          </Typography>
        </TableCell>
        <TableCell sx={{ p: 0, width: 100, textAlign: 'center' }}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            sx={{ height: '100%', minHeight: 48, gap: 1 }}
          >
            {showDeleteButton && (
              <IconButton 
                aria-label='delete'
                color='error'
                onClick={handleDeleteClick}
                data-testid={`delete-admin-${admin._id}`}
                sx={{ color: '#e57373', cursor: 'pointer', m: 0, p: 0.5 }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {!isActive && (
              <IconButton aria-label='more'
                aria-controls={menuOpen ? 'admin-menu' : undefined}
                aria-haspopup='true'
                onClick={handleMenuClick}
                color='success'
                sx={{ color: 'success.main', cursor: 'pointer', m: 0, p: 0.5 }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
            <Menu id='admin-menu' anchorEl={menuAnchorEl} keepMounted open={menuOpen} onClose={handleMenuClose}>
              <MenuItem onClick={handleResendInvitation} disabled={isResending}>
                {isResending ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <CircularProgress size={16} />
                    {t('Sending...')}
                  </Box>
                ) : (
                  t('Resend Invitation')
                )}
              </MenuItem>
            </Menu>
          </Box>
        </TableCell>
      </TableRow>
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title={t('Delete School Admin')}
        message={
          t('Are you sure you want to remove admin permissions for <name> <email>?',
            {
              name: admin.firstName + ' ' + admin.lastName,
              email: admin.email
            })
        }
        onConfirm={handleConfirmDelete}
        onCloseModal={handleConfirmDialogClose}
        confirmButtonText={t('Delete')}
        confirmButtonColor='error'
      />
    </>
  );
};

export default SchoolAdminItem;
